import { Briefcase, Phone, PhoneOff } from "lucide-react";
import { useTranslations } from "use-intl";
import { CallActionButton } from "@/components/communications/call-action-button";
import { PersonAvatar } from "@/components/person-avatar";
import { useApp } from "@/lib/context/use-powersync";
import { useStaffCallerLookup } from "@/lib/hooks/use-staff-caller-lookup";
import {
  cn,
  getInitialsFromDisplayName,
  getStaffDisplayName,
} from "@/lib/utils";

/**
 * Internal-call counterpart to <IncomingCallView>. Same layout, but:
 *   - looks the caller up via membership → staff instead of phone → guest
 *   - shows an "Internal" label instead of "Phone"/"WhatsApp"
 *   - no reservation/unit pill (staff don't have one)
 *
 * Rendered when `pendingInvite.kind === "staff"`. The caller's membership
 * id is decoded from the Twilio identity at invite time and stored on the
 * PendingInvite — we just consume it here.
 */
export interface IncomingStaffCallViewProps {
  onAccept: () => void;
  onReject: () => void;
  /** Caller's membership id, decoded from their `<member>__<property>` identity. */
  staffMemberId: string | undefined;
  /** The name the call carried, shown until the caller's profile syncs. */
  staffName?: string;
}

export function IncomingStaffCallView({
  staffMemberId,
  staffName,
  onAccept,
  onReject,
}: IncomingStaffCallViewProps) {
  const t = useTranslations("communications.incomingStaffCallView");
  const { currentProperty } = useApp();
  const { staff } = useStaffCallerLookup(staffMemberId);

  const callerName = (staff && getStaffDisplayName(staff)) || staffName;
  const displayName = callerName ?? t("internalCall");
  const initials = callerName ? getInitialsFromDisplayName(callerName) : "";
  const avatarSrc = staff?.image ?? undefined;

  return (
    <div
      className={cn(
        "flex h-full flex-col",
        "bg-gradient-to-b from-primary/10 via-background to-background",
        "px-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+2rem)]"
      )}
    >
      <div className="flex items-center justify-center gap-2 text-muted-foreground">
        <Briefcase aria-hidden="true" className="h-3.5 w-3.5" />
        <p className="font-medium text-xs uppercase tracking-[0.18em]">
          {t("header")}
        </p>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <div className="relative flex h-32 w-32 items-center justify-center">
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-primary/30 motion-safe:animate-ping"
          />
          <span
            aria-hidden="true"
            className="absolute inset-2 rounded-full bg-primary/20 [animation-delay:600ms] motion-safe:animate-ping"
          />
          <PersonAvatar
            alt={displayName}
            avatar={avatarSrc}
            className="relative z-10 shadow-xl"
            initials={initials}
            size="2xl"
          />
        </div>

        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="font-semibold text-3xl text-foreground">
            {displayName}
          </h1>
          <p className="text-muted-foreground text-sm">
            {currentProperty?.name
              ? t("internalAtProperty", { property: currentProperty.name })
              : t("internal")}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-around gap-6">
        <CallActionButton
          icon={<PhoneOff className="size-6" />}
          label={t("decline")}
          onClick={onReject}
          testId="incoming-staff-call-decline"
          variant="destructive"
        />
        <CallActionButton
          icon={<Phone className="size-6" />}
          label={t("accept")}
          onClick={onAccept}
          testId="incoming-staff-call-accept"
          variant="success"
        />
      </div>
    </div>
  );
}
